import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import cn from 'classnames'
import { useNavigate } from '@tanstack/react-router'
import { skipToken, useQuery } from '@tanstack/react-query'
import { Ops } from '@willie/schemas'
import { Badge, Flex, Kbd, Spinner, Text, TextField } from '@radix-ui/themes'
import { MagnifyingGlassIcon } from '@radix-ui/react-icons'
import { useTRPC, type TRPCOutputs } from '~/utils/trpc'
import { computeStage, isRetired } from '~/features/installations/service'
import { stageColor, stageLabel, type Stage } from '~/features/installations/constants'
import { StationTypeIcon } from '~/features/stations/components/StationTypeIcon'

type InstallationListItem = TRPCOutputs['ops']['stationInstallations']['list'][number]

type StationListItem = TRPCOutputs['ops']['stations']['list']['stations'][number]

type OrgResult = { kind: 'org'; id: string; name: string }

type InstallationResult = {
  kind: 'installation'
  id: number
  primary: string
  org: string | null
  site: string | null
  contact: string | null
  internalId: string | null
  loggerSerial: string | null
  stage: Stage
  isStalled: boolean
  isRetired: boolean
}

type StationResult = {
  kind: 'station'
  id: string
  name: string
  internalId: string | null
  site: string
  org: string
  measurementType: StationListItem['measurement_type']
  stationType: StationListItem['station_type']
}

type SpotlightResult = OrgResult | InstallationResult | StationResult

const MIN_QUERY_LENGTH = 2
const MAX_ORG_RESULTS = 5
const MAX_INSTALLATION_RESULTS = 8
const MAX_STATION_RESULTS = 8
const DEBOUNCE_MS = 250
const ORGS_STALE_MS = 1000 * 60 * 5

const SECTION_LABEL: Record<SpotlightResult['kind'], string> = {
  org: 'Organizations',
  installation: 'Installations',
  station: 'Stations'
}

const useDebouncedValue = <T,>(value: T, delayMs: number): T => {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs)
    return () => clearTimeout(timer)
  }, [value, delayMs])
  return debounced
}

const toInstallationResult = (
  item: InstallationListItem,
  orgById: Map<string, string>
): InstallationResult => ({
  kind: 'installation',
  id: item.installation_id,
  primary: item.name || item.internal_id || `Installation #${item.installation_id}`,
  org: item.org_id ? orgById.get(item.org_id) ?? null : null,
  site: item.site?.name ?? null,
  contact: item.site_contact?.name ?? null,
  internalId: item.internal_id,
  loggerSerial: item.logger_serial_number,
  stage: computeStage(item),
  isStalled: item.current_stall != null,
  isRetired: isRetired(item)
})

const toStationResult = (item: StationListItem): StationResult => ({
  kind: 'station',
  id: item.id,
  name: item.name,
  internalId: item.internal_id,
  site: item.site.name,
  org: item.org.name,
  measurementType: item.measurement_type,
  stationType: item.station_type
})

// Mirrors every term of the installations search OR except the org branch, so a direct hit can be
// told apart from a row that came along with its whole organization.
const matchesTerm = (item: InstallationListItem, needle: string): boolean =>
  [
    item.name,
    item.internal_id,
    item.logger_serial_number,
    item.site_contact?.name,
    item.site_contact?.email,
    String(item.installation_id)
  ].some((field) => field?.toLowerCase().includes(needle))

const dedupeById = <T extends { id: string }>(items: T[]): T[] => [
  ...new Map(items.map((item) => [item.id, item])).values()
]

const isEditableTarget = (target: EventTarget | null): boolean => {
  const el = target as HTMLElement | null
  return el?.tagName === 'INPUT' || el?.tagName === 'TEXTAREA' || Boolean(el?.isContentEditable)
}

const OrgRow = ({ result }: { result: OrgResult }) => (
  <>
    <Flex direction="column" gap="1" className="min-w-0 flex-1">
      <Text size="2" weight="medium" truncate>
        {result.name}
      </Text>
      <Text size="1" color="gray">
        Open organization
      </Text>
    </Flex>
    <Badge color="gray" variant="soft" size="1" className="shrink-0">
      Organization
    </Badge>
  </>
)

const InstallationRow = ({ result }: { result: InstallationResult }) => {
  const secondary = [result.site, result.contact, result.internalId, result.loggerSerial]
    .filter(Boolean)
    .join(' · ')

  return (
    <>
      <Text size="1" color="gray" className="w-12 shrink-0 tabular-nums">
        #{result.id}
      </Text>
      <Flex direction="column" gap="1" className="min-w-0 flex-1">
        <Flex align="center" gap="2" className="min-w-0">
          <Text size="2" weight="medium" truncate>
            {result.primary}
          </Text>
          {result.org && (
            <Badge color="gray" variant="soft" size="1" className="max-w-[160px] shrink-0 truncate">
              {result.org}
            </Badge>
          )}
        </Flex>
        <Text size="1" color="gray" truncate>
          {secondary || 'No details'}
        </Text>
      </Flex>
      <Flex align="center" gap="2" className="shrink-0">
        {result.isRetired && (
          <Badge color="gray" variant="soft" size="1">
            Retired
          </Badge>
        )}
        {result.isStalled && (
          <Badge color="red" variant="soft" size="1">
            ⚠ stalled
          </Badge>
        )}
        <Badge color={stageColor[result.stage]} variant="soft" size="1">
          {stageLabel[result.stage]}
        </Badge>
      </Flex>
    </>
  )
}

const StationRow = ({ result }: { result: StationResult }) => (
  <>
    <Flex justify="center" className="w-12 shrink-0">
      <StationTypeIcon measurementType={result.measurementType} stationType={result.stationType} />
    </Flex>
    <Flex direction="column" gap="1" className="min-w-0 flex-1">
      <Flex align="center" gap="2" className="min-w-0">
        <Text size="2" weight="medium" truncate>
          {result.name}
        </Text>
        <Badge color="gray" variant="soft" size="1" className="max-w-[160px] shrink-0 truncate">
          {result.org}
        </Badge>
      </Flex>
      <Text size="1" color="gray" truncate>
        {[result.site, result.internalId].filter(Boolean).join(' · ')}
      </Text>
    </Flex>
  </>
)

const ResultRow = ({ result }: { result: SpotlightResult }) => {
  switch (result.kind) {
    case 'org':
      return <OrgRow result={result} />
    case 'installation':
      return <InstallationRow result={result} />
    case 'station':
      return <StationRow result={result} />
  }
}

export const Spotlight = () => {
  const trpc = useTRPC()
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)

  const [isOpen, setIsOpen] = useState(false)
  const [term, setTerm] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)

  const debouncedTerm = useDebouncedValue(term.trim(), DEBOUNCE_MS)
  const isSearchable = debouncedTerm.length >= MIN_QUERY_LENGTH

  const orgsQuery = useQuery(
    trpc.ops.orgs.list.queryOptions(undefined, { enabled: isOpen, staleTime: ORGS_STALE_MS })
  )

  const orgById = useMemo(
    () => new Map((orgsQuery.data ?? []).map((org) => [org.id, org.name])),
    [orgsQuery.data]
  )

  // Neither installations nor stations are searchable by org name server-side; matching the
  // already-cached org list here is what lets an org name reach both.
  const matchedOrgs = useMemo(() => {
    if (!isSearchable) return []
    const needle = debouncedTerm.toLowerCase()
    return (orgsQuery.data ?? []).filter((org) => org.name.toLowerCase().includes(needle))
  }, [orgsQuery.data, debouncedTerm, isSearchable])

  const matchedOrgIds = matchedOrgs.slice(0, Ops.StationInstallations.MAX_SEARCH_ORG_IDS).map((org) => org.id)

  const orgResults: OrgResult[] = matchedOrgs
    .slice(0, MAX_ORG_RESULTS)
    .map((org) => ({ kind: 'org', id: org.id, name: org.name }))

  const installationsQuery = useQuery(
    trpc.ops.stationInstallations.list.queryOptions(
      {
        search: debouncedTerm,
        searchOrgIds: matchedOrgIds.length > 0 ? matchedOrgIds : undefined,
        includeRetired: true
      },
      { enabled: isOpen && isSearchable }
    )
  )
  const stationsQuery = useQuery(
    trpc.ops.stations.list.queryOptions({ q: debouncedTerm }, { enabled: isOpen && isSearchable })
  )
  // `stations` reaches its org only through an embed, which PostgREST cannot OR against the name filter.
  const stationsByOrgQuery = useQuery(
    trpc.ops.stations.list.queryOptions(matchedOrgIds.length > 0 ? { orgIds: matchedOrgIds } : skipToken, {
      enabled: isOpen && isSearchable
    })
  )

  // An org-name match returns that org's whole fleet, ordered by installation_id; without this the
  // oldest of them would fill the list and bury the installation the term actually names.
  const installationResults = useMemo<InstallationResult[]>(() => {
    const needle = debouncedTerm.toLowerCase()
    return [...(installationsQuery.data ?? [])]
      .sort(
        (a, b) =>
          Number(matchesTerm(b, needle)) - Number(matchesTerm(a, needle)) ||
          Number(isRetired(a)) - Number(isRetired(b))
      )
      .slice(0, MAX_INSTALLATION_RESULTS)
      .map((item) => toInstallationResult(item, orgById))
  }, [installationsQuery.data, orgById, debouncedTerm])

  const stationResults = useMemo<StationResult[]>(
    () =>
      dedupeById([
        ...(stationsQuery.data?.stations ?? []).slice(0, MAX_STATION_RESULTS),
        ...(stationsByOrgQuery.data?.stations ?? []).slice(0, MAX_STATION_RESULTS)
      ])
        .slice(0, MAX_STATION_RESULTS)
        .map(toStationResult),
    [stationsQuery.data, stationsByOrgQuery.data]
  )

  const flatResults: SpotlightResult[] = [...orgResults, ...installationResults, ...stationResults]

  useEffect(() => setActiveIndex(0), [debouncedTerm])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const isCmdK = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k'
      const isSlash = e.key === '/' && !isEditableTarget(e.target)
      if (!isCmdK && !isSlash) return
      e.preventDefault()
      setIsOpen(true)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const close = () => {
    setIsOpen(false)
    setTerm('')
    setActiveIndex(0)
  }

  const select = (result: SpotlightResult) => {
    close()
    switch (result.kind) {
      case 'org':
        navigate({ to: '/organizations', search: { orgId: result.id } })
        return
      case 'installation':
        navigate({ to: '/installations/$installationId', params: { installationId: String(result.id) } })
        return
      case 'station':
        navigate({ to: '/stations/$stationId', params: { stationId: result.id } })
    }
  }

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      close()
      return
    }
    if (!flatResults.length) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((i) => (i + 1) % flatResults.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((i) => (i - 1 + flatResults.length) % flatResults.length)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const active = flatResults[activeIndex] ?? flatResults[0]
      if (active) select(active)
    }
  }

  if (!isOpen) return null

  const isLoading = orgsQuery.isLoading || installationsQuery.isLoading || stationsQuery.isLoading

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Search organizations, installations and stations"
      onMouseDown={close}
      className="fixed inset-0 z-50 flex justify-center bg-[var(--black-a9)] px-4 pt-[15vh] backdrop-blur-sm"
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        className="flex h-fit w-full max-w-[640px] flex-col overflow-hidden rounded-[var(--radius-4)] border border-gray-5 bg-[var(--color-panel-solid)] shadow-6"
      >
        <TextField.Root
          ref={inputRef}
          size="3"
          autoFocus
          value={term}
          placeholder="Search organizations, installations & stations…"
          className="border-0 shadow-none"
          onChange={(e) => setTerm(e.target.value)}
          onKeyDown={onInputKeyDown}
        >
          <TextField.Slot>
            <MagnifyingGlassIcon width={18} height={18} />
          </TextField.Slot>
          <TextField.Slot side="right">
            <Kbd size="1">esc</Kbd>
          </TextField.Slot>
        </TextField.Root>

        {isSearchable && (
          <div className="border-t border-t-gray-5">
            {isLoading ? (
              <Flex align="center" gap="2" px="4" py="4">
                <Spinner size="1" />
                <Text size="2" color="gray">
                  Searching…
                </Text>
              </Flex>
            ) : flatResults.length === 0 ? (
              <Text as="div" size="2" color="gray" className="px-4 py-4">
                No matches for “{debouncedTerm}”
              </Text>
            ) : (
              <div className="max-h-[50vh] overflow-y-auto py-1">
                {flatResults.map((result, index) => (
                  <Fragment key={`${result.kind}-${result.id}`}>
                    {result.kind !== flatResults[index - 1]?.kind && (
                      <Text as="div" size="1" color="gray" weight="medium" className="px-4 pb-1 pt-2">
                        {SECTION_LABEL[result.kind]}
                      </Text>
                    )}
                    <button
                      type="button"
                      onMouseEnter={() => setActiveIndex(index)}
                      onClick={() => select(result)}
                      className={cn(
                        'flex w-full items-center gap-3 px-4 py-2 text-left',
                        index === activeIndex ? 'bg-[var(--accent-a3)]' : 'hover:bg-[var(--gray-a3)]'
                      )}
                    >
                      <ResultRow result={result} />
                    </button>
                  </Fragment>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
